import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleInfo,
  faGear,
  faImage,
  faPen,
  faUserGear,
  faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCircleInfo as faCircleInfoOutline,
  faGear as faGearOutline,
  faImage as faImageOutline,
  faPen as faPenOutline,
  faUserGear as faUserGearOutline,
  faUserPlus as faUserPlusOutline,
} from '@fortawesome/pro-regular-svg-icons';
import { useTranslations } from 'next-intl';
import { AccessLevel } from 'models/AccessLevel';
import { Box, MemberRole } from 'models/Box';
import { openBoxDetailsModal, openBoxModal } from 'xstate/actors/ModalActor';
import { BoxModalStep } from 'xstate/machines/ModalMachine';

export type BoxActionKey =
  | 'details'
  | 'edit'
  | 'appearance'
  | 'manageAccess'
  | 'sharingSettings'
  | 'settings';

export type BoxAction = {
  key: BoxActionKey;
  // Solid, for menu rows.
  icon: IconProp;
  // Outline, for toolbars.
  outlineIcon: IconProp;
  label: string;
  onClick: () => void;
};

// What the user can do with a box, from its capabilities: the rows of the
// box menu in the Memory Boxes column, and the toolbar of the overview bar
// on narrower screens. Deletion lives in the settings' danger zone, so
// there is no Delete action here (as on Mac).
export const useBoxActions = (box: Box): BoxAction[] => {
  const t = useTranslations();

  const { capabilities, role } = box.permissions;
  const canEdit = capabilities.canEdit;
  const canManageAccess =
    box.accessLevel === AccessLevel.Shared && capabilities.canManagePermissions;
  // Settings (access level, comments and reactions, deletion) is owner
  // territory: the API refuses editors and viewers on those endpoints.
  const canOpenSettings = capabilities.canManagePermissions;
  const isGuest = role !== MemberRole.Owner;

  const actions: BoxAction[] = [];

  actions.push({
    key: 'details',
    icon: faCircleInfo,
    outlineIcon: faCircleInfoOutline,
    label: t('boxes.menu.details'),
    onClick: () => openBoxDetailsModal(box),
  });

  if (canEdit) {
    actions.push({
      key: 'edit',
      icon: faPen,
      outlineIcon: faPenOutline,
      label: t('common.edit'),
      onClick: () => openBoxModal(box),
    });
    actions.push({
      key: 'appearance',
      icon: faImage,
      outlineIcon: faImageOutline,
      label: t('boxes.menu.appearance'),
      onClick: () =>
        openBoxModal(box, {
          step: BoxModalStep.Appearance,
          canNavigateBack: false,
        }),
    });
  }

  if (canManageAccess) {
    actions.push({
      key: 'manageAccess',
      icon: faUserPlus,
      outlineIcon: faUserPlusOutline,
      label: t('boxes.menu.manageAccess'),
      onClick: () =>
        openBoxModal(box, {
          step: BoxModalStep.ManagePermissions,
          canNavigateBack: false,
        }),
    });
  }

  if (isGuest) {
    actions.push({
      key: 'sharingSettings',
      icon: faUserGear,
      outlineIcon: faUserGearOutline,
      label: t('boxes.menu.sharingSettings'),
      onClick: () =>
        openBoxModal(box, {
          step: BoxModalStep.SharingSettings,
          canNavigateBack: false,
        }),
    });
  }

  if (canOpenSettings) {
    actions.push({
      key: 'settings',
      icon: faGear,
      outlineIcon: faGearOutline,
      label: t('boxSettings.title'),
      onClick: () => openBoxModal(box, { step: BoxModalStep.Settings }),
    });
  }

  return actions;
};
